/* eslint-env node */

const RADIUS_BALL_OF_CLAY_METRES = 6378137.0;
const TO_RAD_CONST = Math.PI / 180;
const WORLD_TILE_DIMENSION = 256.0;
const MAGIC_NUMBER = (0.5 * WORLD_TILE_DIMENSION) / Math.PI;
const BASE_Z = 20.0;

export default {
  isValidWgs84(wgs84) {
    if (wgs84.lat !== null && wgs84.lat !== undefined && wgs84.lon !== null && wgs84.lon !== undefined) {
      const lat = parseFloat(wgs84.lat);
      const lon = parseFloat(wgs84.lon);
      return lat > -90 && lat < 90 && lon > -180 && lon < 180;
    }
    return false;
  },

  /** converts a 2D point (x/y, in meters) to a WGS84 coordinate (lat/lon)
   *  an anchor-point is defined which represents the "top left" corner of a bounding box
   *
   * - point2d: the 2D point you want to translate to a WGS84 coordinate, defined in metres from the top-left
   * - anchor: the top left WGS84 coordinate.
   */
  toWgs84(point2d, anchor, rotation = undefined) {
    const rotationRad = (rotation === undefined ? 0 : rotation) * TO_RAD_CONST;
    const rotatedX = Math.cos(rotationRad) * point2d.x - Math.sin(rotationRad) * point2d.y;
    const rotatedY = Math.sin(rotationRad) * point2d.x + Math.cos(rotationRad) * point2d.y;

    const points2dPerMetre = this.points2dPerMetre(anchor.lat);

    // distances in 2D-points of the final WGS84 coordinate relative to the anchored point
    const distanceX = rotatedX * points2dPerMetre;
    const distanceY = rotatedY * points2dPerMetre;
    const anchorDistance = Math.sqrt(Math.pow(distanceX, 2) + Math.pow(distanceY, 2));

    const anchorBearing = this.angleBetween({ x: 0, y: 0 }, point2d);
    const relativeX = anchorDistance * Math.sin(anchorBearing);
    const relativeY = anchorDistance * Math.cos(anchorBearing);

    const anchorPoint2d = this.wgs84ToPoint2d(anchor);
    const calcPoint2dX = anchorPoint2d.x + relativeX;
    const calcPoint2dY = anchorPoint2d.y - relativeY;

    return this.point2dToWgs84({ x: calcPoint2dX, y: calcPoint2dY });
  },
  /** converts a WGS84 coordinate (lat/lon) to a normed 2D point (x/y) between [0,1]
   *  an anchor-point is defined which represents the "top left" corner of a boundingbox as well as dimensions in metres
   *
   * - wgs84: the WGS84 coordinate you want to translate to a 2D point
   * - dimensions: The dimensions in metres of the bounds
   * - anchor: the top left WGS84 coordinate of the northed bounds
   */
  toPoint2d(wgs84, dimensions, anchor) {
    const bearing = this.bearingFromTo(anchor, wgs84);

    const distance = this.distanceBetweenWgs84(anchor, wgs84);
    const distanceX = distance * Math.sin(bearing);
    const distanceY = /* y axis is inversed */ -distance * Math.cos(bearing);

    return { x: distanceX / dimensions.width, y: distanceY / dimensions.height };
  },

  /** converts a normed 2D point (x/y between [0..1]) to a WGS84 coordinate (lat/lon)
   *  the bounding-box defines the south-west and north-east WGS84 coordinate, where the north-west is (0,0) in 2D points
   *
   * - normedPoint2d: the normed 2D point you want to translate to a WGS84 coordinate, defined in the range [0..1]
   * - boundingBox: the bounding-box with south-west and north-east WGS84 coordinates
   */
  wgs84FromNormedPoint2d(normedPoint2d, boundingBox) {
    const northWest = { lat: boundingBox.northEast.lat, lon: boundingBox.southWest.lon };
    const dimensions = this.boundsDimensions(boundingBox);

    return this.toWgs84({ x: normedPoint2d.x * dimensions.width, y: normedPoint2d.y * dimensions.height }, northWest);
  },
  /** converts a WGS84 coordinate (lat/lon) to a normed 2D point (x/y) between [0,1]
   *  the bounding-box defines the south-west and north-east WGS84 coordinate, where the north-west is (0,0) in 2D points
   *
   * - wgs84: the WGS84 coordinate you want to translate to a 2D point
   * - boundingBox: the bounding-box with south-west and north-east WGS84 coordinates
   */
  normedPoint2dFromWgs84(wgs84, boundingBox) {
    const northWest = { lat: boundingBox.northEast.lat, lon: boundingBox.southWest.lon };
    return this.toPoint2d(wgs84, this.boundsDimensions(boundingBox), northWest);
  },

  boundsDimensions(boundingBox) {
    const swPoint2d = this.wgs84ToPoint2d(boundingBox.southWest);
    const nePoint2d = this.wgs84ToPoint2d(boundingBox.northEast);

    const dimX = Math.abs(swPoint2d.x - nePoint2d.x) * this.metresPerPoint2d(boundingBox.southWest.lat);
    const dimY = Math.abs(swPoint2d.y - nePoint2d.y) * this.metresPerPoint2d(boundingBox.southWest.lat);

    return { width: dimX, height: dimY };
  },

  boundsZoomLevel(bounds, mapDim) {
    const ZOOM_MAX = 21;

    function latRad(lat) {
      const sin = Math.sin(lat * TO_RAD_CONST);
      const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
      return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
    }

    function zoom(mapPx, worldPx, fraction) {
      return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
    }

    const ne = bounds.northEast;
    const sw = bounds.southWest;

    const latFraction = (latRad(ne.lat) - latRad(sw.lat)) / Math.PI;

    const lngDiff = ne.lon - sw.lon;
    const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

    const latZoom = zoom(mapDim.height, WORLD_TILE_DIMENSION, latFraction);
    const lngZoom = zoom(mapDim.width, WORLD_TILE_DIMENSION, lngFraction);

    return Math.min(latZoom, lngZoom, ZOOM_MAX);
  },

  bearingFromTo(start, target) {
    const lat1 = start.lat * TO_RAD_CONST;
    const lon1 = start.lon * TO_RAD_CONST;
    const lat2 = target.lat * TO_RAD_CONST;
    const lon2 = target.lon * TO_RAD_CONST;

    const dLon = lon2 - lon1;

    const y = Math.sin(dLon) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);

    let bearing = Math.atan2(y, x);
    bearing += 2 * Math.PI;

    while (bearing > 2 * Math.PI) {
      bearing -= 2 * Math.PI;
    }
    while (bearing < 0) {
      bearing += 2 * Math.PI;
    }
    return bearing;
  },

  distanceBetweenWgs84(wgs84a, wgs84b) {
    const c1LatRad = wgs84a.lat * TO_RAD_CONST;
    const c2LatRad = wgs84b.lat * TO_RAD_CONST;

    const latDelta = (wgs84b.lat - wgs84a.lat) * TO_RAD_CONST;
    const lonDelta = (wgs84b.lon - wgs84a.lon) * TO_RAD_CONST;
    const a =
      Math.sin(latDelta / 2) * Math.sin(latDelta / 2) +
      Math.cos(c1LatRad) * Math.cos(c2LatRad) * Math.sin(lonDelta / 2) * Math.sin(lonDelta / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = RADIUS_BALL_OF_CLAY_METRES * c;
    return distance;
  },
  wgs84FromWithBearingAndDistance(wgs84, bearing, distance) {
    const latRad = wgs84.lat * TO_RAD_CONST;
    const lonRad = wgs84.lon * TO_RAD_CONST;

    const lat2 = Math.asin(
      Math.sin(latRad) * Math.cos(distance / RADIUS_BALL_OF_CLAY_METRES) +
        Math.cos(latRad) * Math.sin(distance / RADIUS_BALL_OF_CLAY_METRES) * Math.cos(bearing)
    );
    const lon2 =
      lonRad +
      Math.atan2(
        Math.sin(bearing) * Math.sin(distance / RADIUS_BALL_OF_CLAY_METRES) * Math.cos(latRad),
        Math.cos(distance / RADIUS_BALL_OF_CLAY_METRES) - Math.sin(latRad) * Math.sin(lat2)
      );

    const lat = lat2 / TO_RAD_CONST;
    const lon = lon2 / TO_RAD_CONST;
    return { lat, lon };
  },
  angleBetween(point2d1, point2d2) {
    const angle = Math.atan2(point2d2.y - point2d1.y, point2d2.x - point2d1.x) + Math.PI / 2;
    return angle;
  },

  wgs84ToPoint2d(wgs84) {
    const lonRad = wgs84.lon * TO_RAD_CONST;
    const x = MAGIC_NUMBER * Math.pow(2, BASE_Z) * (lonRad + Math.PI);

    const latRad = wgs84.lat * TO_RAD_CONST;
    const tanCalculation = Math.tan(Math.PI / 4 + latRad / 2.0);
    const y = MAGIC_NUMBER * Math.pow(2, BASE_Z) * (Math.PI - Math.log(tanCalculation));

    return { x, y };
  },

  point2dToWgs84(point2D) {
    const zoomPowerRadians = Math.pow(2, BASE_Z) * TO_RAD_CONST;
    let lon = point2D.x / (zoomPowerRadians * MAGIC_NUMBER) - 180.0;
    while (lon < -180) {
      lon += 180;
    }
    while (lon > 180) {
      lon -= 180;
    }

    const expBeforeTan = Math.exp(Math.PI - point2D.y / (Math.pow(2, BASE_Z) * MAGIC_NUMBER));
    const latRad = 2 * Math.atan(expBeforeTan) - Math.PI / 2;

    let lat = latRad / TO_RAD_CONST;
    while (lat < -90) {
      lat += 90;
    }
    while (lat > 90) {
      lat -= 90;
    }

    return { lat, lon };
  },

  metresPerPoint2d(lat) {
    const latRad = lat * TO_RAD_CONST;
    return (Math.cos(latRad) * 2 * Math.PI * RADIUS_BALL_OF_CLAY_METRES) / (WORLD_TILE_DIMENSION * Math.pow(2, BASE_Z));
  },
  points2dPerMetre(lat) {
    return 1 / this.metresPerPoint2d(lat);
  },
};
